import { finalize, Observable } from 'rxjs';
import { ChangeDetectionStrategy, Component, inject, output, Signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';

/**
 * interfaces
 */
import { IUnitTypeFormControls, ICreateUnitTypePayload, IUpdateUnitTypePayload } from '../../interfaces';

/**
 * enums
 */
import { OperationModifyMode } from '@core/enums/operation-mode';

/**
 * consts
 */
import { UnitTypesListTranslateMapper } from '../../pages';

/**
 * services
 */
import { UnitTypeFormService, UnitTypeService } from '../../services';

/**
 * shared
 */
import { SwitchComponent } from '@forms/components/switch/switch.component';
import { TextareaComponent } from '@forms/components/textarea/textarea.component';
import { InputFieldComponent } from '@forms/components/input-field/input-field.component';

@Component({
  selector: 'app-unit-type-form',
  templateUrl: './unit-type-form.component.html',
  styleUrl: './unit-type-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    /**
     * libs
     */
    TranslatePipe,
    /**
     * shared
     */
    SwitchComponent,
    TextareaComponent,
    InputFieldComponent,
  ],
  providers: [
    UnitTypeFormService,
  ],
})
export class UnitTypeFormComponent {

  // services
  private readonly _unitTypeService = inject(UnitTypeService);
  private readonly _formService     = inject(UnitTypeFormService);

  /**
   * TRANSLATE_KEY_MAP
   */
  public readonly TRANSLATE_KEY_MAP = UnitTypesListTranslateMapper;

  /** Emits the created / updated record ID on successful submit. */
  public readonly submitted = output<string>();

  /** True while a create / update / getById API call is in flight. */
  public readonly isLoading: Signal<boolean> = this._formService.isLoading;

  /** True when the form is in create mode (vs. update). */
  public readonly isCreationMode: Signal<boolean> = this._formService.isCreationMode;

  /** The reactive form group exposed to the template. */
  public get operationsForm(): FormGroup<IUnitTypeFormControls> {
    return this._formService.form;
  }

  // ─── Lifecycle init helpers ───────────────────────────────────────────────

  /** Prepares the form for creating a new unit type. */
  public initCreate(): void {
    this._formService.setOperationMode(OperationModifyMode.Create);
    this._formService.setSelectedId(null);
    this._formService.resetForm();
  }

  /** Fetches the record by ID and prepares the form for editing. */
  public initEdit(id: string): void {
    this._formService.setOperationMode(OperationModifyMode.Update);
    this._formService.setSelectedId(id);
    this._formService.setLoading(true);
    this._unitTypeService
      .getById(id)
      .pipe(finalize(() => this._formService.setLoading(false)))
      .subscribe((dto) => this._formService.patchValue(dto));
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  /** Validates the form and triggers the create or update API call. */
  public submit(): void {

    // void
    if (this.operationsForm.invalid) {
      this.operationsForm.markAllAsTouched();
      return;
    }

    // start loading
    this._formService.setLoading(true);

    // call apis
    this._operationCallbackApi()
      .pipe(finalize(() => this._formService.setLoading(false)))
      .subscribe({
        next: (id) => {
          this._formService.resetForm();
          this.submitted.emit(id);
        },
      });
  }

  /** Resets the form state without emitting any event. */
  public cancel(): void {
    this._formService.resetForm();
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  private _operationCallbackApi(): Observable<string> {
    const value = this.operationsForm.getRawValue();

    const payload: ICreateUnitTypePayload = {
      name:                       value.name!,
      description:                value.description!,
      default_level:              value.default_level!,
      icon_id:                    value.icon_id ?? null,
      default_type_for_its_level: value.default_type_for_its_level ?? false,
    };

    if (this.isCreationMode()) {
      return this._unitTypeService.create(payload);
    }

    const id = this._formService.selectedId();
    if (id == null) throw new Error('Unit Type ID is required for update');

    const updatePayload: IUpdateUnitTypePayload = { ...payload };
    return this._unitTypeService.update(id, updatePayload);
  }

}
